import "./styles.scss";

import ScraperConfirm from "components/contacts/scraper/confirm";
import Button from "components/ui/button";
import Dialog from "components/ui/dialog";
import CleanButton from "components/utils/CleanButton";
import bdaylytics from "components/utils/analytics/bdaylytics";
import { cdnURL } from "components/utils/cdn";
import { cloudspongeProviderMap } from "components/utils/cloudsponge";
import { removeDuplicateContacts } from "components/utils/contact";
import { useApp, useQueryDict } from "hooks/app";
import { useCloudsponge } from "hooks/cloudsponge";
import { useMember } from "hooks/member";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { AiOutlineGoogle } from "react-icons/ai";
import { IoLogoYahoo } from "react-icons/io";
import { TiVendorMicrosoft } from "react-icons/ti";
import { addContacts, checkContacts } from "services/contact-service";

const Scraper = ({
  onSuccess,
  buttonProps,
  buttonTitle,
  title,
  className,
  confirmTitle,
  confirmSubtitle,
  confirmInfo,
  viralBirthdayRequestId,
  confirmButtonTitle,
  marketingId,
  skipBirthdayRequests,
}) => {
  const { emailProvider, memberId } = useMember();
  const { showAlert } = useApp();
  const { search } = useQueryDict();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [importedContacts, setImportedContacts] = useState([]);
  const [existingContacts, setExistingContacts] = useState([]);
  const [errorContacts, setErrorContacts] = useState([]);

  const { cloudspongeLoaded, cloudspongeError, launchCloudsponge } = useCloudsponge();

  const providerGroup = cloudspongeProviderMap[emailProvider?.emailProviderKey];
  const buttonLoading = !cloudspongeLoaded || !!cloudspongeError || isSubmitting;

  const launchScraper = () => {
    launchCloudsponge(emailProvider?.emailProviderKey, async (data) => {
      await checkImportedContacts(data);
    });
  };

  const launchScraperFull = () => {
    launchCloudsponge(null, async (data) => {
      await checkImportedContacts(data);
    });
  };

  const checkImportedContacts = async ({ source, contacts }) => {
    setErrorMessage("");
    const trackingData = { search, count: contacts.length };
    bdaylytics.track("Scraper Selected Contacts", { ...trackingData, source }, "SCRAPER");
    const mappedContacts = contacts.map((contact, i) => ({
      firstName: contact.first_name,
      lastName: contact.last_name,
      email: contact.email.length > 0 ? contact.email.find((e) => e.selected === true).address : "",
      keepAgeSecret: false,
      index: i,
    }));
    const cleanedContacts = removeDuplicateContacts(mappedContacts);
    setIsSubmitting(true);
    const payload = { contacts: cleanedContacts, requestingMemberId: memberId, requiredCode: 3 };
    const { errors, ...result } = await checkContacts(payload);
    if (errors?.length > 0) {
      setErrorMessage(errors[0].errorMessage);
    } else if (result.contacts?.length > 0) {
      const newContacts = result.contacts.filter((c) => ["new", "exists_new_name", "exists_email_only"].includes(c.status));
      const existingContacts = result.contacts
        .filter((c) => c.status === "exists_full" && c.originalContact?.contactId)
        .map((c) => ({ ...c, contactId: c.originalContact.contactId }));
      const errorContacts = result.contacts.filter((c) => c.status === "error");
      setExistingContacts(existingContacts);
      setErrorContacts(errorContacts);
      if (newContacts?.length > 0) {
        // Add any new contacts
        await onAddContacts(newContacts);
      } else if (existingContacts?.length > 0) {
        setIsSubmitting(false);
        onImportSuccess();
      } else if (errorContacts?.length > 0) {
        setIsSubmitting(false);
        onImportError();
      } else {
        // Nothing to do here
        setIsSubmitting(false);
        onScraperConfirmSuccess();
      }
    }
  };

  const onAddContacts = async (contacts) => {
    const payload = { contacts, requestingMemberId: memberId, requiredCode: 3 };
    const { errors, ...result } = await addContacts(payload);
    if (errors?.length > 0) {
      setErrorMessage(errors[0].errorMessage);
    } else if (result.contacts?.length > 0) {
      const contacts = result.contacts.filter((c) => c.status === "added");
      if (contacts && contacts.length) {
        const trackingData = { search, count: contacts.length };
        bdaylytics.track("New Contact", trackingData, "SCRAPER", "Key Metrics");
      }
      setImportedContacts(contacts.filter((c) => c.status === "added"));
    }
    setIsSubmitting(false);
    onImportSuccess();
  };

  const onImportSuccess = () => {
    if (!skipBirthdayRequests) {
      // Open confirmation for birthday requests
      setConfirmDialogOpen(true);
    } else {
      onSuccess();
    }
  };

  const onImportError = () => {
    if (!skipBirthdayRequests) {
      // Open confirmation to show contact errors
      setConfirmDialogOpen(true);
    } else {
      onSuccess();
    }
  };

  const onScraperConfirmSuccess = (payload) => {
    if (payload) {
      showAlert({ message: "Birthday request has been sent." });
    }
    setConfirmDialogOpen(false);
    onSuccess(payload);
  };

  return (
    <div className={`scraper-container ${title ? "scraper-inline" : ""}`}>
      <div className="scraper-wrapper">
        {title ? (
          <CleanButton onClick={launchScraperFull} className={`scraper-title ${className}`}>
            {title}
          </CleanButton>
        ) : (
          <div>
            <Button className="scraper-button" onClick={launchScraper} disabled={buttonLoading} {...buttonProps}>
              {buttonTitle ? (
                <span>{buttonTitle}</span>
              ) : (
                <>
                  {emailProvider && providerGroup?.canLaunch ? (
                    <>
                      {emailProvider.emailProviderKey === "gmail" && <AiOutlineGoogle className="scraper-button-icon google-icon" />}
                      {emailProvider.emailProviderKey === "yahoo" && <IoLogoYahoo className="scraper-button-icon yahoo-icon" />}
                      {emailProvider.emailProviderKey === "outlook" && <TiVendorMicrosoft className="scraper-button-icon outlook-icon" />}
                      <span>Select {emailProvider.emailProviderName} Contacts</span>
                    </>
                  ) : (
                    <span>Select Contacts</span>
                  )}
                </>
              )}
            </Button>
            {(errorMessage || cloudspongeError) && (
              <div className="form-error-message">
                <span>{errorMessage || cloudspongeError}</span>
              </div>
            )}
            {providerGroup?.canLaunch && (
              <div className="scraper-options-toggle" onClick={launchScraperFull} tabIndex="0" role="button">
                <span>
                  <i>import other contacts</i>
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <Dialog open={isSubmitting} title="" subtitle="" preventClose>
        <img src={cdnURL("img/rebuild/icons/spinner.gif?v=1")} alt="" className="request-birthday-spinner" />
      </Dialog>
      <Dialog className="scraper-confirmation-dialog" open={confirmDialogOpen} title="" subtitle="" onClose={() => setConfirmDialogOpen(false)}>
        <div className="confirmation-dialog-container">
          {confirmTitle}
          {confirmSubtitle}
          {confirmInfo}
          <ScraperConfirm
            buttonTitle={confirmButtonTitle}
            marketingId={marketingId}
            viralBirthdayRequestId={viralBirthdayRequestId}
            onSuccess={onScraperConfirmSuccess}
            importedContacts={importedContacts}
            existingContacts={existingContacts}
            errorContacts={errorContacts}
          />
        </div>
      </Dialog>
    </div>
  );
};

Scraper.propTypes = {
  onSuccess: PropTypes.func.isRequired,
  viralBirthdayRequestId: PropTypes.number,
  marketingId: PropTypes.number,
  buttonTitle: PropTypes.string,
  buttonProps: PropTypes.object,
  title: PropTypes.node,
  className: PropTypes.string,
  confirmTitle: PropTypes.node,
  confirmSubtitle: PropTypes.node,
  confirmInfo: PropTypes.node,
  confirmButtonTitle: PropTypes.string,
  skipBirthdayRequests: PropTypes.bool,
};

Scraper.defaultProps = {
  buttonTitle: "",
  buttonProps: {},
  title: undefined,
  className: "",
  confirmTitle: <h4 className="confirmation-title">Final step,</h4>,
  confirmSubtitle: undefined,
  confirmInfo: <span className="confirmation-info">{`Below are the contacts you selected. Send them a birthday request and we'll do the rest!`}</span>,
  confirmButtonTitle: `Send {0} Birthday Requests`,
  skipBirthdayRequests: false,
};

export default Scraper;
